import { isLiteTemplate } from "@lexicanon/shared"
import type { TProcessingStage } from "../../lib/pipeline-progress"
import { friendlyStepForId } from "../../lib/pipeline-steps"

export type TFinalizeMode = "full" | "lite"

const FULL_STEPS: readonly TProcessingStage[] = ["uploading", "transcribing", "identifying_speakers", "analyzing"]

/** Fourth step is the title. `lite-finalize` emits `analyzing` for it. */
const LITE_STEPS: readonly TProcessingStage[] = ["uploading", "transcribing", "identifying_speakers", "analyzing"]

/** How far the creep may approach the next step without arriving there. */
const STEP_GAP = 0.01

/** Default in-step creep per tick, in percentage points. */
export const STEP_CREEP = 0.4

export function stepsForMode(mode: TFinalizeMode): readonly TProcessingStage[] {
  return mode === "lite" ? LITE_STEPS : FULL_STEPS
}

/** Explicit `mode` wins. Otherwise `meeting-lite` is Lite, and anything else is Full. */
export function resolveFinalizeMode(input: { mode?: TFinalizeMode | null; templateId?: string | null }): TFinalizeMode {
  if (input.mode === "lite" || input.mode === "full") return input.mode
  return isLiteTemplate(input.templateId) ? "lite" : "full"
}

/**
 * Stage this mode is allowed to show. Lite and Full list the same four
 * stages. Lite's last one is the title (`lite-finalize` emits `analyzing`).
 * A stage outside the list stays on the last step until Done.
 */
export function clampStageToMode(stage: TProcessingStage, mode: TFinalizeMode): TProcessingStage {
  if (stage === "complete") return "complete"
  const steps = stepsForMode(mode)
  if (steps.includes(stage)) return stage
  return steps[steps.length - 1] ?? "uploading"
}

/**
 * Stage the overlay should highlight. The coarse stage wins when a step id
 * would rewind it (audio compression runs after the summary). A step id may
 * only move the highlight forward. No stage yet starts at saving the audio.
 */
export function resolveDisplayedStage(
  stage: TProcessingStage | null,
  mode: TFinalizeMode,
  stepRef?: string | null,
): TProcessingStage {
  const fromStage = clampStageToMode(stage ?? "uploading", mode)
  if (fromStage === "complete" || !stepRef) return fromStage
  const fromRef = clampStageToMode(friendlyStepForId(stepRef), mode)
  const steps = stepsForMode(mode)
  const stageIdx = steps.indexOf(fromStage)
  const refIdx = steps.indexOf(fromRef)
  if (refIdx > stageIdx) return fromRef
  return fromStage
}

export interface IStepPosition {
  index: number
  total: number
  isComplete: boolean
}

/** `index` is the active step. On Done it equals `total`, so every step reads as finished. */
export function activeStepIndex(stage: TProcessingStage | null, mode: TFinalizeMode): IStepPosition {
  const steps = stepsForMode(mode)
  if (stage === "complete") return { index: steps.length, total: steps.length, isComplete: true }
  const clamped = clampStageToMode(stage ?? "uploading", mode)
  const index = steps.indexOf(clamped)
  return { index: index < 0 ? 0 : index, total: steps.length, isComplete: false }
}

export interface IStepRange {
  /** Inclusive start of this step, as a percentage of the whole. */
  start: number
  /** Highest percentage this step may show. Strictly below the next step's start. */
  ceiling: number
}

/**
 * Step `stepIndex` of `total` occupies `[start, next start)`. The ceiling stops
 * a hair under the next step so step 3 never reads as step 4. Done is 100.
 */
export function stepProgressRange(stepIndex: number, total: number, isComplete: boolean): IStepRange {
  if (isComplete || total <= 0) return { start: 100, ceiling: 100 }
  const index = Math.max(0, Math.min(stepIndex, total - 1))
  const start = (index / total) * 100
  const next = ((index + 1) / total) * 100
  return { start, ceiling: Math.max(start, next - STEP_GAP) }
}

/**
 * Next displayed percentage.
 * Below the current step's start → jump to the start (the step moved forward).
 * Above the ceiling → snap back to the start (the step moved backward).
 * Otherwise creep, and never pass the ceiling. Done is 100.
 */
export function nextStepPercent(current: number, range: IStepRange, isComplete: boolean, creep = STEP_CREEP): number {
  if (isComplete) return 100
  if (current < range.start || current > range.ceiling) return range.start
  return Math.min(range.ceiling, current + creep)
}

/** Whole percent for the label. Stays at 99 until the meeting is actually done. */
export function displayPercent(percent: number, isComplete: boolean): number {
  if (isComplete) return 100
  if (!Number.isFinite(percent)) return 0
  return Math.min(99, Math.max(0, Math.floor(percent)))
}
